import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";

function normalizeTaxId(taxId) {
  const raw = String(taxId || "").trim();
  if (!raw) return "";
  return raw.replace(/[^0-9./-]/g, "").trim();
}

function detectTaxIdLabel(taxId, language) {
  const digits = String(taxId || "").replace(/\D/g, "");
  if (digits.length === 11) return "CPF";
  if (digits.length === 14) return "CNPJ";
  const isPt = (language || "").toLowerCase().startsWith("pt");
  return isPt ? "CPF/CNPJ" : "CPF/CNPJ";
}

function shouldLogTerms() {
  try {
    if (typeof window === "undefined") return false;
    const sp = new URLSearchParams(window.location.search);
    if (sp.get("debugTerms") === "1") return true;
    if (window.localStorage?.getItem("debugTerms") === "1") return true;
  } catch (_) {
    // ignore
  }
  return false;
}

function maskTaxId(value) {
  const raw = String(value || "");
  const digits = raw.replace(/\D/g, "");
  if (!digits) return "";
  const last4 = digits.slice(-4);
  return `***${last4}`;
}

export default function DraftRiskTermsDocument({
  language,
  buyerName,
  clientName,
  projectTitle, // currently unused (context shown by caller if needed)
  taxId,
  titleVariant = "h5",
}) {
  const isPt = (language || "").toLowerCase().startsWith("pt");

  const { docTitle, intro, items, conclusion, dateLine, signatureLine } = useMemo(() => {
    const normalizedTaxId = normalizeTaxId(taxId);
    const taxIdLabel = normalizedTaxId ? detectTaxIdLabel(normalizedTaxId, language) : "";

    // Buyer name should appear only in the intro sentence (per request).
    // If buyerName is empty, do NOT fall back to clientName.
    const introName = String(buyerName || "").trim();
    const hasBuyerName = introName.length > 0;

    if (shouldLogTerms()) {
      console.info("[terms][DraftRiskTermsDocument] name resolution", {
        language,
        isPt,
        buyerName: String(buyerName || "").trim(),
        clientName: String(clientName || "").trim(),
        introName,
        hasTaxId: !!normalizedTaxId,
        taxIdMasked: maskTaxId(normalizedTaxId),
        projectTitle: projectTitle || "",
      });
    }

    const idFragment = hasBuyerName && normalizedTaxId
      ? isPt
        ? `, inscrito no ${taxIdLabel} sob o nº ${normalizedTaxId}`
        : `, REGISTERED WITH THE ${taxIdLabel} under No. ${normalizedTaxId}`
      : "";

    if (isPt) {
      return {
        docTitle: "TERMO DE ADESÃO E DE CIÊNCIA E RISCO",
        intro:
          (hasBuyerName
            ? `Por meio deste instrumento eu, ${introName}, doravante denominado “Cliente”, DECLARO estar ciente dos seguintes riscos, não taxativos, relacionados à realização da compra de token por meio da plataforma de tokenização INNOVA INVESTMENTS CONSULTORIA LTDA, com sede na cidade de São Paulo, Estado de São Paulo, no Viaduto Nove de Julho, nº 160, Conj 26 B, Centro, CEP 01050-060, em conformidade com o art. 26, inciso IV, da Resolução da Comissão de Valores Mobiliários nº 88, de 27 de abril de 2022 (respectivamente “CVM” e “Resolução CVM 88”):`
            : `Por meio deste instrumento eu, doravante denominado “Cliente”, DECLARO estar ciente dos seguintes riscos, não taxativos, relacionados à realização da compra de token por meio da plataforma de tokenização INNOVA INVESTMENTS CONSULTORIA LTDA, com sede na cidade de São Paulo, Estado de São Paulo, no Viaduto Nove de Julho, nº 160, Conj 26 B, Centro, CEP 01050-060, em conformidade com o art. 26, inciso IV, da Resolução da Comissão de Valores Mobiliários nº 88, de 27 de abril de 2022 (respectivamente “CVM” e “Resolução CVM 88”):`),
        items: [
          "da possibilidade de perda da totalidade do capital investido em decorrência do insucesso da sociedade empresária de pequeno porte;",
          "quando aplicável, do risco advindo da aquisição ou da conversão dos valores mobiliários de que sou titular em participação em sociedades empresárias de pequeno porte que, dependendo do tipo societário adotado, podem acarretar riscos ao meu patrimônio pessoal em razão de sua responsabilidade patrimonial limitada não ser reconhecida em decisões judiciais nas esferas trabalhistas, previdenciária e tributária, entre outras;",
          "dos riscos associados à detenção de posição minoritária na sociedade empresária de pequeno porte, considerando a influência que os seus controladores possam vir a exercer em eventos corporativos como a emissão adicional de valores mobiliários, alienação do controle ou de ativos, e transações com partes relacionadas;",
          "do risco de crédito da sociedade empresária de pequeno porte, quando da emissão de títulos representativos de dívida;",
          "do risco associado às dificuldades que possa enfrentar para vender valores mobiliários de sociedade empresária de pequeno porte não registrada na CVM e que não são admitidos à negociação em mercados regulamentados;",
          "que a plataforma apenas presta o serviço de apresentação dos projetos, não sendo responsável pelas informações fornecidades pelo autor dos tokens e nem pela condução dos projetos à serem realizados pelos autores dos tokens, cabendo única e exclusivamente ao Cliente, a avaliação dos risco dos autores dos tokens e dos projetos apresentados;",
          "de que a sociedade empresária de pequeno porte não é registrada na CVM e que pode não haver prestação de informações contínuas pela sociedade após a realização da oferta; e",
          "de que não existe obrigação, definida em lei ou regulamentação, da sociedade empresária de pequeno porte que não seja constituída como sociedade anônima em transformar-se neste tipo de sociedade.",
        ],
        conclusion:
          "O Cliente, por este ato, garante que leu e tomou ciência dos riscos envolvidos, bem como declara também que a realização de operações comandadas pelo Cliente será de sua única e exclusiva responsabilidade.",
        dateLine: "São Paulo - SP, 6 de janeiro, 2026",
        signatureLine: introName,
      };
    }

    return {
      docTitle: "TERM OF ADHESION AND SCIENCE AND RISK",
      intro:
        (hasBuyerName
          ? `By means of this instrument, I, ${introName}, hereinafter referred to as "Client", DECLARE to be aware of the following risks, not exhaustive, related to the purchase of tokens through the tokenization platform INNOVA INVESTMENTS CONSULTORIA LTDA, headquartered in the city of São Paulo, State of São Paulo, at Viaduto Nove de Julho, No. 160, Conj 26 B, Centro, CEP 01050-060, in accordance with article 26, item IV, of Resolution No. 88 of the Brazilian Securities and Exchange Commission Resolution No. 88, of April 27, 2022 (respectively "CVM" and "CVM Resolution 88"):`
          : `By means of this instrument, I, hereinafter referred to as "Client", DECLARE to be aware of the following risks, not exhaustive, related to the purchase of tokens through the tokenization platform INNOVA INVESTMENTS CONSULTORIA LTDA, headquartered in the city of São Paulo, State of São Paulo, at Viaduto Nove de Julho, No. 160, Conj 26 B, Centro, CEP 01050-060, in accordance with article 26, item IV, of Resolution No. 88 of the Brazilian Securities and Exchange Commission Resolution No. 88, of April 27, 2022 (respectively "CVM" and "CVM Resolution 88"):`),
      items: [
        "the possibility of losing all the capital invested as a result of the failure of the small business company;",
        "when applicable, the risk arising from the acquisition or conversion of the securities I hold into participation in small business companies which, depending on the corporate type adopted, may entail risks to my personal assets due to their limited patrimonial liability not being recognized in judicial decisions in the labor, social security and tax spheres, among others;",
        "the risks associated with holding a minority position in the small business company, considering the influence that its controlling shareholders may exert on corporate events such as the additional issuance of securities, sale of control or assets, and transactions with related parties;",
        "the credit risk of the small business company, when issuing debt securities;",
        "the risk associated with the difficulties it may face in selling securities of small business companies not registered with the CVM and which are not admitted to trading in regulated markets;",
        "that the platform only provides the service of presenting the projects, and is not responsible for the information provided by the author of the tokens or for the conduct of the projects to be carried out by the authors of the tokens, and it is solely and exclusively up to the Client to assess the risks of the authors of the tokens and the projects presented;",
        "that the small business company is not registered with the CVM and that there may not be continuous provision of information by the company after the offering is made; and",
        "that there is no obligation, defined by law or regulation, of the small business company that is not constituted as a corporation to become this type of company.",
      ],
      conclusion:
        "The Client, by this act, guarantees that it has read and become aware of the risks involved, as well as declares that the execution of operations commanded by the Client will be its sole and exclusive responsibility.",
      dateLine: "São Paulo - SP, January 6, 2026",
      signatureLine: introName,
    };
  }, [buyerName, clientName, taxId, isPt, language]);

  return (
    <Box>
      <Typography variant={titleVariant} sx={{ fontWeight: 800, color: "#194578", mb: 2 }}>
        {docTitle}
      </Typography>

      <Typography variant="body2" sx={{ color: "#111827", lineHeight: 1.7, mb: 2 }}>
        {intro}
      </Typography>

      <Box component="ol" sx={{ pl: 3, mb: 2 }} type="a">
        {items.map((text, idx) => (
          <li key={idx}>
            <Typography variant="body2" sx={{ color: "#111827", lineHeight: 1.7, mb: 1 }}>
              {text}
            </Typography>
          </li>
        ))}
      </Box>

      <Typography variant="body2" sx={{ color: "#111827", lineHeight: 1.7, mb: 2 }}>
        {conclusion}
      </Typography>

      <Typography variant="body2" sx={{ color: "#111827", mt: 3 }}>
        {dateLine}
      </Typography>
      <Typography variant="body2" sx={{ color: "#111827", mt: 3, fontWeight: 700 }}>
        {signatureLine}
      </Typography>
    </Box>
  );
}
